import styled from 'styled-components';
import { getFontStyles } from '@/redux/cmsConfig/utils';

interface Props {
  value: {
    background_color?: string | null;
    hide_label?: boolean;
    label: string;
    text_color?: string | null;
    icon_image_url?: string | null;
  } | null;
  /**
   * `overlay` (default) pins the tag to the top-left of the product image.
   * `inline` keeps it in the normal flow, e.g. in a row of tags under the product name, styled
   * like a dietary preference tag unless the tag brings its own colours.
   */
  variant?: 'overlay' | 'inline';
}

export function Tag({ value, variant = 'overlay' }: Props) {
  if (!value) {
    return null;
  }

  const content = (
    <>
      {value.icon_image_url && <Icon src={value.icon_image_url} alt={`${value.label} Tag Icon`} />}
      {!value.hide_label ? value.label : ''}
    </>
  );

  if (variant === 'inline') {
    return (
      <InlineContainer
        $backgroundColor={value.background_color ?? null}
        $textColor={value.text_color ?? null}
      >
        {content}
      </InlineContainer>
    );
  }

  return (
    <Container
      backgroundColor={value.background_color ?? 'inherit'}
      textColor={value.text_color ?? 'inherit'}
    >
      {content}
    </Container>
  );
}

interface StyleProps {
  backgroundColor: string;
  textColor: string;
}

const Container = styled.span<StyleProps>(({ backgroundColor, textColor }) => ({
  alignItems: 'center',
  background: backgroundColor,
  borderRadius: '0px var(--size-1) var(--size-1) 0px',
  color: textColor,
  display: 'flex',
  fontSize: '.625rem',
  justifyContent: 'center',
  left: 0,
  padding: 'var(--size-1) var(--size-2)',
  position: 'absolute',
  top: 'var(--size-2)',
}));

interface InlineStyleProps {
  $backgroundColor: string | null;
  $textColor: string | null;
}

const InlineContainer = styled.span<InlineStyleProps>(
  ({ theme, $backgroundColor, $textColor }) => ({
    alignItems: 'center',
    backgroundColor:
      $backgroundColor ?? theme.dietary_preferences_tag.background_color ?? 'transparent',
    border: '1px solid',
    borderColor: theme.dietary_preferences_tag.border_color ?? 'transparent',
    borderRadius: theme.dietary_preferences_tag.border_radius ?? 0,
    display: 'inline-flex',
    fontWeight: 500,
    padding: 'var(--size-1) var(--size-2)',
    ...getFontStyles(theme.dietary_preferences_tag.font, {
      font_size: () => '.75rem',
      line_height: () => '1',
    }),
    ...($textColor ? { color: $textColor } : {}),
  }),
);

const Icon = styled.img({
  maxHeight: '1.25rem',
  marginRight: 'var(--size-1)',
});
